import React, {
	Children,
	cloneElement,
	createContext,
	isValidElement,
	memo,
	useContext,
	useEffect,
	useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import { ReactSerializer, type ReactSerializerInit } from '@atlaskit/renderer';

import { fg } from '@confluence/feature-gating';

export class ProgressiveReactSerializer extends ReactSerializer {
	/**
	 * A cached value of whether version 3 of progressive rendering is enabled.
	 */
	private isV3Enabled: boolean;
	/**
	 * A cached value of whether SSR progressive serialization is enabled.
	 */
	private isSSRProgressiveSerializationEnabled: boolean;
	/**
	 * The first invalid index (after a sequence of valid indices) of a node to batch render.
	 * After `serializeFragment` completes for the root node, this will be the number of nodes to batch render.
	 */
	private maxRenderIndex: number;
	private batchSize: number;

	constructor(init: ReactSerializerInit, batchSize: number) {
		super(init);

		this.isV3Enabled = fg('cc_complexit_fe_progressive_adf_rendering_v3');
		this.isSSRProgressiveSerializationEnabled = fg(
			'cc_complexit_fe_progessive_rendering_ssr_serial',
		);
		this.maxRenderIndex = 0;
		this.batchSize = batchSize;
	}

	/**
	 * @override
	 */
	serializeFragment(
		fragment: SerializeFragmentParameters[0],
		props?: SerializeFragmentParameters[1],
		_target?: SerializeFragmentParameters[2],
		key?: SerializeFragmentParameters[3],
		parentInfo?: SerializeFragmentParameters[4],
	): JSX.Element | null {
		const isRoot = key === undefined; // Like ReactSerializer.serializeFragment

		if (isRoot) {
			// Like ReactSerializer.resetState
			this.maxRenderIndex = 0;
		}

		const renderIndex = this.maxRenderIndex++;

		const shouldRenderNodeProgressively =
			!shouldSkipForWorkaround(fragment, props, parentInfo) &&
			!(this.isV3Enabled && shouldSkipForV3(parentInfo));

		const shouldSkipNodeSerialization =
			shouldRenderNodeProgressively &&
			this.isSSRProgressiveSerializationEnabled &&
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR &&
			renderIndex > this.batchSize; // Skip serialization of anything beyond the first batch for SSR

		if (shouldSkipNodeSerialization) {
			return null;
		}

		const nodeElement = super.serializeFragment.apply(
			this,
			arguments as unknown as SerializeFragmentParameters,
		);

		if (!nodeElement || !shouldRenderNodeProgressively) {
			return nodeElement;
		}

		if (isRoot) {
			return (
				<RootNodeRenderer maxRenderIndex={this.maxRenderIndex} batchSize={this.batchSize}>
					{nodeElement}
				</RootNodeRenderer>
			);
		} else {
			return (
				<NodeRenderer key={key} renderIndex={renderIndex}>
					{nodeElement}
				</NodeRenderer>
			);
		}
	}
}

/**
 * Cases in which node elements created by the super `ReactSerializer` should not be preogressively rendered
 * in order to avoid breakages of functionality.
 */
function shouldSkipForWorkaround(
	fragment: SerializeFragmentParameters[0],
	props: SerializeFragmentParameters[1],
	parentInfo: SerializeFragmentParameters[4],
) {
	return (
		// Some node components rely on the type of children. Do not wrap such children.
		// For example, there is an expectation in the table component(s) in `isHeaderRowEnabled` of `TableHeader`.
		props?.nodeType === 'tableHeader' ||
		// There is an expectation in the table component(s) in `isHeaderRowEnabled` about the children of the first row.
		isHeaderTableRow(fragment, props, parentInfo)
	);
}

/**
 * Version 3 of progressive rendering renders top level nodes and table rows of top level tables progressively.
 * All other nodes are rendered as soon as their ancestors are rendered.
 */
function shouldSkipForV3(parentInfo?: SerializeFragmentParameters[4]) {
	return (
		// Top level nodes have a parent path of 0 or 1 because of the root node.
		!(parentInfo?.path?.length === 0 || parentInfo?.path?.length === 1) &&
		// Top level tables table rows have a parent path of 2 because of the root node and the table node.
		// The expected structure is: root -> table -> tableRow -> tableHeader || tableCell
		!(parentInfo?.path?.length === 2 && parentInfo?.path?.[0].type?.name === 'table')
	);
}

function isHeaderTableRow(
	fragment: SerializeFragmentParameters[0],
	props: SerializeFragmentParameters[1],
	parentInfo: SerializeFragmentParameters[4],
) {
	try {
		if (props?.nodeType === 'tableRow' && parentInfo) {
			const parentPath = parentInfo?.path;
			if (parentPath?.length > 1) {
				const tableNode = parentPath[parentPath.length - 2];
				if (
					tableNode?.type?.name === 'table' &&
					parentPath[parentPath.length - 1]?.type?.name === 'tableRow' &&
					tableNode.maybeChild(0)?.content === fragment
				) {
					return true;
				}
			}
		}
	} catch (e) {
		if (process.env.NODE_ENV !== 'production') {
			throw e;
		}
	}
	return false;
}

const NodeRenderer = memo(({ children, renderIndex, ...props }: NodeRendererProps) => {
	const maxBatchRenderIndex = useContext(NodeRendererContext);
	const shouldRender = !maxBatchRenderIndex || renderIndex < maxBatchRenderIndex;

	if (!shouldRender) {
		return null;
	}

	// Some node components add props to their children, outside of `serializeFragment`. Pass such props through:
	if (Object.keys(props).length) {
		return Children.map(children, (child) =>
			isValidElement(child) ? cloneElement(child, props) : child,
		);
	}

	return children;
});

type NodeRendererProps = PropsWithChildren<{
	renderIndex: number;
}>;

const NodeRendererContext = createContext<number>(0);

const RootNodeRenderer = memo(({ children, maxRenderIndex, batchSize }: RootNodeRendererProps) => {
	const [batchIndex, setBatchIndex] = useState(0);

	const lastBatchIndex = Math.max(Math.ceil(maxRenderIndex / batchSize), 1) - 1; // The last valid batch index

	useEffect(() => {
		let cancelHandle: number | undefined;

		if (batchIndex < lastBatchIndex) {
			const requestIdleCallback = window.requestIdleCallback || window.requestAnimationFrame;
			cancelHandle = requestIdleCallback(() =>
				setBatchIndex((batchIndex) => (batchIndex < lastBatchIndex ? batchIndex + 1 : batchIndex)),
			);
		}

		return () => {
			if (cancelHandle !== undefined) {
				const cancelIdleCallback = window.cancelIdleCallback || window.cancelAnimationFrame;
				cancelIdleCallback(cancelHandle);
			}
		};
	}, [batchIndex, lastBatchIndex, setBatchIndex]);

	const maxBatchRenderIndex = (batchIndex + 1) * batchSize;

	return (
		<NodeRendererContext.Provider value={maxBatchRenderIndex}>
			{children}
		</NodeRendererContext.Provider>
	);
});

type RootNodeRendererProps = PropsWithChildren<{
	maxRenderIndex: number;
	batchSize: number;
}>;

type SerializeFragmentParameters = Parameters<ReactSerializer['serializeFragment']>;
